import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
    ArrowDownCircle, ArrowUpCircle, Search, Camera, ImagePlus,
    RotateCw, Plus, Trash2, X, ChevronLeft, ChevronRight, DollarSign
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───── 360° Viewer Component ───── */
const Viewer360 = ({ images, fallbackImage, onAddPhotos }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const containerRef = useRef(null);

    const imgs = images && images.length > 0 ? images : [];
    const hasMultiple = imgs.length > 1;

    const handleStart = useCallback((clientX) => {
        if (!hasMultiple) return;
        setIsDragging(true);
        setStartX(clientX);
    }, [hasMultiple]);

    const handleMove = useCallback((clientX) => {
        if (!isDragging || !hasMultiple) return;
        const diff = clientX - startX;
        const threshold = 30;
        if (Math.abs(diff) > threshold) {
            setCurrentIndex(prev => {
                if (diff > 0) return prev === 0 ? imgs.length - 1 : prev - 1;
                return prev >= imgs.length - 1 ? 0 : prev + 1;
            });
            setStartX(clientX);
        }
    }, [isDragging, startX, hasMultiple, imgs.length]);

    const handleEnd = useCallback(() => setIsDragging(false), []);

    useEffect(() => {
        const onMouseUp = () => setIsDragging(false);
        window.addEventListener('mouseup', onMouseUp);
        window.addEventListener('touchend', onMouseUp);
        return () => { window.removeEventListener('mouseup', onMouseUp); window.removeEventListener('touchend', onMouseUp); };
    }, []);

    const currentSrc = imgs.length > 0 ? `${API_URL}${imgs[currentIndex]?.url}` : (fallbackImage || null);

    return (
        <div ref={containerRef} className="relative aspect-square bg-zinc-800 overflow-hidden select-none"
            style={{ cursor: hasMultiple ? (isDragging ? 'grabbing' : 'grab') : 'default', touchAction: 'pan-y' }}
            onMouseDown={e => handleStart(e.clientX)}
            onMouseMove={e => handleMove(e.clientX)}
            onMouseUp={handleEnd}
            onTouchStart={e => handleStart(e.touches[0].clientX)}
            onTouchMove={e => handleMove(e.touches[0].clientX)}
            onTouchEnd={handleEnd}
        >
            {currentSrc ? (
                <img src={currentSrc} alt="Diseño" className="w-full h-full object-cover pointer-events-none" draggable={false} />
            ) : (
                <div className="flex flex-col items-center justify-center h-full gap-2 text-zinc-500" onClick={onAddPhotos} style={{ cursor: onAddPhotos ? 'pointer' : 'default' }}>
                    <div className="w-16 h-16 rounded-full border-2 border-dashed border-zinc-600 flex items-center justify-center mb-1">
                        <Camera className="w-8 h-8" />
                    </div>
                    <span className="text-sm font-medium text-zinc-400">Sin fotos</span>
                    {onAddPhotos && <span className="text-xs text-amber-400 font-medium">Toca para agregar</span>}
                </div>
            )}
            {hasMultiple && (
                <>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 rounded-full px-3 py-1">
                        <RotateCw className="w-3 h-3 text-amber-400" />
                        <span className="text-[10px] text-zinc-300 font-medium">
                            {currentIndex + 1}/{imgs.length}
                        </span>
                    </div>
                    <button onClick={() => setCurrentIndex(i => i === 0 ? imgs.length - 1 : i - 1)}
                        className="absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 text-white hover:bg-amber-400 hover:text-zinc-900 transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={() => setCurrentIndex(i => i >= imgs.length - 1 ? 0 : i + 1)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-black/50 text-white hover:bg-amber-400 hover:text-zinc-900 transition-colors">
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </>
            )}
        </div>
    );
};

/* ───── Main Page ───── */
export const Playeras = () => {
    const [movimientos, setMovimientos] = useState([]);
    const [inventario, setInventario] = useState([]);
    const [disenos, setDisenos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState('entrada');
    const [foto360ModalOpen, setFoto360ModalOpen] = useState(false);
    const [selectedDesign, setSelectedDesign] = useState(null);
    const [uploading, setUploading] = useState(false);
    // Modal de PRECIOS
    const [preciosOpen, setPreciosOpen] = useState(false);
    const [preciosForm, setPreciosForm] = useState({ estampado: '', color: '', precio: '', precio_paquete: '', precio_corrida: '', tamano_paquete: 12, marca: '' });
    const openPrecios = (d) => {
        const designData = getDesignData(d.estampado, d.color);
        setPreciosForm({
            estampado: d.estampado,
            color: d.color,
            precio: designData?.precio ?? '',
            precio_paquete: designData?.precio_paquete ?? '',
            precio_corrida: designData?.precio_corrida ?? '',
            tamano_paquete: designData?.tamano_paquete || 12,
            marca: designData?.marca || ''
        });
        setPreciosOpen(true);
    };
    const guardarPrecios = async () => {
        try {
            await axios.put(`${API_URL}/api/playeras/diseno/precios`, {
                estampado: preciosForm.estampado,
                color: preciosForm.color,
                precio: parseFloat(preciosForm.precio) || 0,
                precio_paquete: parseFloat(preciosForm.precio_paquete) || 0,
                precio_corrida: parseFloat(preciosForm.precio_corrida) || 0,
                tamano_paquete: parseInt(preciosForm.tamano_paquete) || 12,
                marca: preciosForm.marca || '',
            });
            toast.success('Precios guardados');
            setPreciosOpen(false);
            loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    };
    const [searchTerm, setSearchTerm] = useState('');
    const [colores, setColores] = useState([]);
    const [tallas, setTallas] = useState([]);
    const [estampados, setEstampados] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const fileInputRef = useRef(null);
    const foto360InputRef = useRef(null);

    const initialForm = {
        fecha: new Date().toISOString().split('T')[0],
        color: '', talla: '', estampado: '', cantidad: '',
        proveedor_id: '', lote_id: '', costo_total: '',
        destino: '', entrega_nombre: '', recibe_nombre: '', observaciones: ''
    };
    const [form, setForm] = useState(initialForm);

    useEffect(() => { loadAll(); }, []);

    const loadAll = async () => {
        try {
            const [movRes, invRes, disRes, colRes, talRes, estRes, provRes] = await Promise.all([
                axios.get(`${API_URL}/api/playeras`),
                axios.get(`${API_URL}/api/playeras/inventario`),
                axios.get(`${API_URL}/api/playeras/disenos`),
                axios.get(`${API_URL}/api/catalogos/colores_playera`).catch(() => ({ data: [] })),
                axios.get(`${API_URL}/api/catalogos/tallas_playera`).catch(() => ({ data: [] })),
                axios.get(`${API_URL}/api/catalogos/estampados`).catch(() => ({ data: [] })),
                axios.get(`${API_URL}/api/proveedores`)
            ]);
            setMovimientos(movRes.data);
            setInventario(invRes.data);
            setDisenos(disRes.data || []);
            setColores(colRes.data || []);
            setTallas(talRes.data || []);
            setEstampados(estRes.data || []);
            setProveedores(provRes.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const openModal = (type) => { setModalType(type); setForm(initialForm); setModalOpen(true); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.color || !form.talla || !form.estampado || !form.cantidad) {
            toast.error('Complete color, talla, estampado y cantidad'); return;
        }
        try {
            const payload = { ...form, cantidad: parseInt(form.cantidad), costo_total: parseFloat(form.costo_total) || 0 };
            await axios.post(`${API_URL}/api/playeras/${modalType === 'entrada' ? 'entrada' : 'salida'}`, payload);
            toast.success(modalType === 'entrada' ? 'Entrada registrada' : 'Salida registrada');
            setModalOpen(false); loadAll();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al registrar'); }
    };

    /* ── 360° Photo Management ── */
    const open360Modal = (estampado, color) => {
        const design = disenos.find(d => d.estampado === estampado && d.color === color);
        setSelectedDesign({
            estampado, color,
            fotos_360: design?.fotos_360 || [],
            foto_url: design?.foto_url || ''
        });
        setFoto360ModalOpen(true);
    };

    const handleUpload360 = async (e) => {
        const file = e.target.files[0];
        if (!file || !selectedDesign) return;
        setUploading(true);
        try {
            const nextAngle = selectedDesign.fotos_360.length;
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(
                `${API_URL}/api/playeras/diseno/foto360?estampado=${encodeURIComponent(selectedDesign.estampado)}&color=${encodeURIComponent(selectedDesign.color)}&angulo=${nextAngle}`,
                formData, { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success(`Ángulo ${nextAngle + 1} subido`);
            // Reload designs and update selected
            const disRes = await axios.get(`${API_URL}/api/playeras/disenos`);
            setDisenos(disRes.data || []);
            const updated = disRes.data.find(d => d.estampado === selectedDesign.estampado && d.color === selectedDesign.color);
            if (updated) setSelectedDesign({ ...selectedDesign, fotos_360: updated.fotos_360 || [], foto_url: updated.foto_url || '' });
        } catch (e) { toast.error('Error al subir foto'); }
        finally { setUploading(false); if (foto360InputRef.current) foto360InputRef.current.value = ''; }
    };

    const handleDelete360 = async (angulo) => {
        if (!selectedDesign) return;
        try {
            await axios.delete(`${API_URL}/api/playeras/diseno/foto360?estampado=${encodeURIComponent(selectedDesign.estampado)}&color=${encodeURIComponent(selectedDesign.color)}&angulo=${angulo}`);
            toast.success('Ángulo eliminado');
            const disRes = await axios.get(`${API_URL}/api/playeras/disenos`);
            setDisenos(disRes.data || []);
            const updated = disRes.data.find(d => d.estampado === selectedDesign.estampado && d.color === selectedDesign.color);
            if (updated) setSelectedDesign({ ...selectedDesign, fotos_360: updated.fotos_360 || [] });
            else setSelectedDesign({ ...selectedDesign, fotos_360: [] });
        } catch (e) { toast.error('Error al eliminar'); }
    };

    /* ── Legacy single photo upload ── */
    const [uploadingDesign, setUploadingDesign] = useState(null);
    const handleFotoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !uploadingDesign) return;
        try {
            const formData = new FormData();
            formData.append('file', file);
            await axios.post(
                `${API_URL}/api/playeras/diseno/foto?estampado=${encodeURIComponent(uploadingDesign.estampado)}&color=${encodeURIComponent(uploadingDesign.color)}`,
                formData, { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Foto actualizada'); loadAll();
        } catch (e) { toast.error('Error al subir foto'); }
        setUploadingDesign(null); if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const getDesignData = (estampado, color) => disenos.find(d => d.estampado === estampado && d.color === color);

    // Group inventory by design
    const designGroups = {};
    inventario.forEach(item => {
        const key = `${item.estampado}|${item.color}`;
        if (!designGroups[key]) designGroups[key] = { estampado: item.estampado, color: item.color, variantes: [], totalStock: 0 };
        designGroups[key].variantes.push({ talla: item.talla, cantidad: item.cantidad });
        designGroups[key].totalStock += item.cantidad;
    });
    const designs = Object.values(designGroups).filter(d =>
        d.totalStock > 0 && (d.estampado.toLowerCase().includes(searchTerm.toLowerCase()) || d.color.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const totalEntradas = movimientos.filter(m => m.tipo === 'entrada').reduce((s, m) => s + m.cantidad, 0);
    const totalSalidas = movimientos.filter(m => m.tipo === 'salida').reduce((s, m) => s + m.cantidad, 0);
    const totalStock = inventario.reduce((s, i) => s + Math.max(0, i.cantidad), 0);

    const filteredMov = movimientos.filter(m =>
        (m.color || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.estampado || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="playeras-page">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFotoUpload} className="hidden" />
            <input ref={foto360InputRef} type="file" accept="image/*" onChange={handleUpload360} className="hidden" data-testid="foto360-input" />

            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Playeras</h1>
                    <p className="text-zinc-400 mt-1">Diseños 360°, entradas y salidas</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => openModal('entrada')} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="btn-entrada">
                        <ArrowDownCircle className="w-4 h-4 mr-2" /> Entrada
                    </Button>
                    <Button onClick={() => openModal('salida')} className="bg-red-500 hover:bg-red-600 text-white" data-testid="btn-salida">
                        <ArrowUpCircle className="w-4 h-4 mr-2" /> Salida
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="card"><CardContent className="p-4"><p className="stat-label">Stock Total</p><p className="stat-value">{totalStock}</p></CardContent></Card>
                <Card className="card"><CardContent className="p-4"><p className="stat-label">Entradas</p><p className="stat-value text-emerald-400">{totalEntradas}</p></CardContent></Card>
                <Card className="card"><CardContent className="p-4"><p className="stat-label">Salidas</p><p className="stat-value text-red-400">{totalSalidas}</p></CardContent></Card>
                <Card className="card"><CardContent className="p-4"><p className="stat-label">Diseños</p><p className="stat-value text-amber-400">{designs.length}</p></CardContent></Card>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input placeholder="Buscar por color o estampado..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
            </div>

            <Tabs defaultValue="disenos">
                <TabsList><TabsTrigger value="disenos">Diseños</TabsTrigger><TabsTrigger value="inventario">Inventario</TabsTrigger><TabsTrigger value="movimientos">Movimientos</TabsTrigger></TabsList>

                {/* DESIGNS GALLERY with 360 */}
                <TabsContent value="disenos">
                    {designs.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {designs.map((d, idx) => {
                                const designData = getDesignData(d.estampado, d.color);
                                const fotos360 = designData?.fotos_360 || [];
                                const fallback = designData?.foto_url ? `${API_URL}${designData.foto_url}` : null;
                                const hasPhotos = fotos360.length > 0 || fallback;
                                return (
                                    <Card key={idx} className="card overflow-hidden group" data-testid={`design-card-${idx}`}>
                                        <Viewer360 images={fotos360} fallbackImage={fallback} onAddPhotos={() => open360Modal(d.estampado, d.color)} />
                                        <CardContent className="p-4 space-y-3">
                                            <div className="flex justify-between items-start">
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="font-bold text-zinc-100 text-sm truncate">{d.estampado}</h3>
                                                    <p className="text-xs text-zinc-400">{d.color}</p>
                                                    {/* Precio configurado */}
                                                    {(() => {
                                                        const dd = getDesignData(d.estampado, d.color);
                                                        const p = dd?.precio || 0;
                                                        const pp = dd?.precio_paquete || 0;
                                                        return p > 0 || pp > 0 ? (
                                                            <p className="text-[10px] text-emerald-400 mt-0.5 font-mono">
                                                                ${p.toLocaleString('es-MX')}{pp > 0 ? ` · paq $${pp.toLocaleString('es-MX')}` : ''}
                                                            </p>
                                                        ) : (
                                                            <p className="text-[10px] text-amber-400 mt-0.5 italic">Sin precio configurado</p>
                                                        );
                                                    })()}
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0 ml-2">
                                                    <span className="text-lg font-bold text-amber-400">{d.totalStock}</span>
                                                    <button
                                                        onClick={() => openPrecios(d)}
                                                        className="p-1 rounded text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10 transition"
                                                        title="Configurar precios"
                                                        data-testid={`btn-precios-diseno-${idx}`}
                                                    >
                                                        <DollarSign className="w-3.5 h-3.5" />
                                                    </button>
                                                    <button
                                                        onClick={async () => {
                                                            if (!window.confirm(`Eliminar diseño "${d.estampado}" / ${d.color}? Esta accion borrara la foto y los angulos 360. El inventario existente NO se borra.`)) return;
                                                            try {
                                                                await axios.delete(`${API_URL}/api/playeras/diseno?estampado=${encodeURIComponent(d.estampado)}&color=${encodeURIComponent(d.color)}`);
                                                                toast.success('Diseño eliminado');
                                                                loadAll();
                                                            } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
                                                        }}
                                                        className="p-1 rounded text-red-400 hover:text-red-300 hover:bg-red-500/10 transition"
                                                        title="Eliminar diseño"
                                                        data-testid={`btn-eliminar-diseno-${idx}`}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>
                                            {/* Prominent photo management button */}
                                            <button onClick={() => open360Modal(d.estampado, d.color)}
                                                className={`w-full flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                                    hasPhotos
                                                        ? 'bg-zinc-800 hover:bg-amber-400/20 text-zinc-300 hover:text-amber-400 border border-zinc-700 hover:border-amber-400/50'
                                                        : 'bg-amber-400/10 hover:bg-amber-400/20 text-amber-400 border border-amber-400/30 hover:border-amber-400/60 animate-pulse'
                                                }`}
                                                data-testid={`btn-360-${idx}`}>
                                                {hasPhotos ? (
                                                    <><RotateCw className="w-4 h-4" /> {fotos360.length} ángulos · Gestionar fotos</>
                                                ) : (
                                                    <><Camera className="w-4 h-4" /> Subir Fotos 360°</>
                                                )}
                                            </button>
                                            {fotos360.length > 0 && (
                                                <p className="text-[10px] text-zinc-500 flex items-center gap-1 justify-center">
                                                    <RotateCw className="w-3 h-3" /> Arrastra la imagen para girar
                                                </p>
                                            )}
                                            <div className="flex flex-wrap gap-1.5">
                                                {d.variantes.sort((a, b) => {
                                                    const order = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
                                                    return order.indexOf(a.talla) - order.indexOf(b.talla);
                                                }).map((v, vi) => (
                                                    <div key={vi} className={`px-2 py-1 rounded-md text-xs font-medium border ${
                                                        v.cantidad <= 0 ? 'border-red-400/30 bg-red-400/5 text-red-400'
                                                            : v.cantidad <= 5 ? 'border-yellow-400/30 bg-yellow-400/5 text-yellow-400'
                                                            : 'border-zinc-700 bg-zinc-800 text-zinc-300'
                                                    }`}>{v.talla}: {v.cantidad}</div>
                                                ))}
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    ) : (
                        <Card className="card"><CardContent className="py-12 text-center text-zinc-500">
                            <ImagePlus className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>No hay diseños con stock.</p>
                        </CardContent></Card>
                    )}
                </TabsContent>

                {/* INVENTORY TABLE */}
                <TabsContent value="inventario">
                    <Card className="card"><CardContent className="p-0">
                        <Table><TableHeader><TableRow className="bg-zinc-800/50">
                            <TableHead className="table-header">Color</TableHead>
                            <TableHead className="table-header">Talla</TableHead>
                            <TableHead className="table-header">Estampado</TableHead>
                            <TableHead className="table-header text-right">Cantidad</TableHead>
                        </TableRow></TableHeader><TableBody>
                            {inventario.filter(i => i.cantidad > 0).map((item, idx) => (
                                <TableRow key={idx} className="table-row">
                                    <TableCell className="table-cell"><span className="badge-info">{item.color}</span></TableCell>
                                    <TableCell className="table-cell">{item.talla}</TableCell>
                                    <TableCell className="table-cell">{item.estampado}</TableCell>
                                    <TableCell className="table-cell text-right font-bold"><span className={item.cantidad <= 5 ? 'text-red-400' : 'text-zinc-100'}>{item.cantidad}</span></TableCell>
                                </TableRow>
                            ))}
                            {inventario.filter(i => i.cantidad > 0).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-zinc-500">No hay inventario</TableCell></TableRow>}
                        </TableBody></Table>
                    </CardContent></Card>
                </TabsContent>

                {/* MOVEMENTS TABLE */}
                <TabsContent value="movimientos">
                    <Card className="card"><CardContent className="p-0">
                        <Table><TableHeader><TableRow className="bg-zinc-800/50">
                            <TableHead className="table-header">Fecha</TableHead>
                            <TableHead className="table-header">Tipo</TableHead>
                            <TableHead className="table-header">Color</TableHead>
                            <TableHead className="table-header">Talla</TableHead>
                            <TableHead className="table-header">Estampado</TableHead>
                            <TableHead className="table-header text-right">Cantidad</TableHead>
                        </TableRow></TableHeader><TableBody>
                            {filteredMov.map(m => (
                                <TableRow key={m.id} className="table-row">
                                    <TableCell className="table-cell text-zinc-400">{m.fecha}</TableCell>
                                    <TableCell className="table-cell"><span className={m.tipo === 'entrada' ? 'badge-success' : 'badge-error'}>{m.tipo === 'entrada' ? 'Entrada' : 'Salida'}</span></TableCell>
                                    <TableCell className="table-cell">{m.color}</TableCell>
                                    <TableCell className="table-cell">{m.talla}</TableCell>
                                    <TableCell className="table-cell">{m.estampado}</TableCell>
                                    <TableCell className="table-cell text-right font-bold"><span className={m.tipo === 'entrada' ? 'text-emerald-400' : 'text-red-400'}>{m.tipo === 'entrada' ? '+' : '-'}{m.cantidad}</span></TableCell>
                                </TableRow>
                            ))}
                            {filteredMov.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-500">No hay movimientos</TableCell></TableRow>}
                        </TableBody></Table>
                    </CardContent></Card>
                </TabsContent>
            </Tabs>

            {/* ── 360° Photo Management Modal ── */}
            <Dialog open={foto360ModalOpen} onOpenChange={setFoto360ModalOpen}>
                <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="modal-360">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <RotateCw className="w-5 h-5 text-amber-400" />
                            Fotos 360° — {selectedDesign?.estampado} {selectedDesign?.color}
                        </DialogTitle>
                    </DialogHeader>

                    {selectedDesign && (
                        <div className="space-y-4">
                            {/* Preview */}
                            <div className="rounded-xl overflow-hidden border border-zinc-700">
                                <Viewer360 images={selectedDesign.fotos_360} fallbackImage={selectedDesign.foto_url ? `${API_URL}${selectedDesign.foto_url}` : null} />
                            </div>

                            {/* Angle thumbnails */}
                            <div>
                                <p className="form-label mb-2">Ángulos subidos ({selectedDesign.fotos_360.length})</p>
                                <div className="flex gap-2 flex-wrap">
                                    {selectedDesign.fotos_360.map((f, i) => (
                                        <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border-2 border-zinc-700 group/thumb">
                                            <img src={`${API_URL}${f.url}`} alt={`Ángulo ${i + 1}`} className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity">
                                                <button onClick={() => handleDelete360(f.angulo)} className="text-red-400 hover:text-red-300">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <span className="absolute bottom-0 left-0 right-0 text-center text-[9px] bg-black/70 text-zinc-300 py-0.5">{i + 1}</span>
                                        </div>
                                    ))}

                                    {/* Add button */}
                                    <button
                                        onClick={() => foto360InputRef.current?.click()}
                                        disabled={uploading}
                                        className="w-16 h-16 rounded-lg border-2 border-dashed border-zinc-600 hover:border-amber-400 flex flex-col items-center justify-center gap-0.5 text-zinc-500 hover:text-amber-400 transition-colors"
                                        data-testid="btn-add-angle"
                                    >
                                        {uploading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-400" /> : <Plus className="w-5 h-5" />}
                                        <span className="text-[9px]">{uploading ? 'Subiendo' : 'Agregar'}</span>
                                    </button>
                                </div>
                            </div>

                            <p className="text-xs text-zinc-500">
                                Sube fotos del producto desde diferentes ángulos. Recomendado: 6-12 fotos girando el producto. En la galería podrás arrastrar para girar.
                            </p>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setFoto360ModalOpen(false)}>Cerrar</Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* ── Entrada/Salida Modal ── */}
            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="playera-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {modalType === 'entrada' ? <><ArrowDownCircle className="w-5 h-5 text-emerald-400" /> Registrar Entrada</> : <><ArrowUpCircle className="w-5 h-5 text-red-400" /> Registrar Salida</>}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label className="form-label">Color *</Label>
                                <Select value={form.color} onValueChange={v => setForm({ ...form, color: v })}>
                                    <SelectTrigger data-testid="select-color"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{colores.map(c => <SelectItem key={c.id} value={c.nombre}>{c.nombre}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="form-group"><Label className="form-label">Talla *</Label>
                                <Select value={form.talla} onValueChange={v => setForm({ ...form, talla: v })}>
                                    <SelectTrigger data-testid="select-talla"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{tallas.map(t => <SelectItem key={t.id} value={t.nombre}>{t.nombre}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label className="form-label">Estampado *</Label>
                                <Select value={form.estampado} onValueChange={v => setForm({ ...form, estampado: v })}>
                                    <SelectTrigger data-testid="select-estampado"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{estampados.map(e => <SelectItem key={e.id} value={e.nombre}>{e.nombre}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="form-group"><Label className="form-label">Cantidad *</Label>
                                <Input type="number" min="1" value={form.cantidad} onChange={e => setForm({ ...form, cantidad: e.target.value })} placeholder="0" data-testid="input-cantidad" />
                            </div>
                        </div>
                        <div className="form-group"><Label className="form-label">Fecha</Label>
                            <Input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} />
                        </div>
                        {modalType === 'entrada' && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group"><Label className="form-label">Proveedor</Label>
                                    <Select value={form.proveedor_id} onValueChange={v => setForm({ ...form, proveedor_id: v })}>
                                        <SelectTrigger><SelectValue placeholder="Opcional" /></SelectTrigger>
                                        <SelectContent>{proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div className="form-group"><Label className="form-label">Costo ($)</Label>
                                    <Input type="number" min="0" step="0.01" value={form.costo_total} onChange={e => setForm({ ...form, costo_total: e.target.value })} placeholder="0.00" />
                                </div>
                            </div>
                        )}
                        {modalType === 'salida' && (
                            <div className="form-group"><Label className="form-label">Destino</Label>
                                <Input value={form.destino} onChange={e => setForm({ ...form, destino: e.target.value })} placeholder="Ej: Tienda, Cliente" />
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label className="form-label">Entrega</Label>
                                <Input value={form.entrega_nombre} onChange={e => setForm({ ...form, entrega_nombre: e.target.value })} placeholder="Quién entrega" />
                            </div>
                            <div className="form-group"><Label className="form-label">Recibe</Label>
                                <Input value={form.recibe_nombre} onChange={e => setForm({ ...form, recibe_nombre: e.target.value })} placeholder="Quién recibe" />
                            </div>
                        </div>
                        <div className="form-group"><Label className="form-label">Observaciones</Label>
                            <Input value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} placeholder="Notas" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className={modalType === 'entrada' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'} data-testid="submit-playera">
                                {modalType === 'entrada' ? 'Registrar Entrada' : 'Registrar Salida'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Modal de PRECIOS */}
            <Dialog open={preciosOpen} onOpenChange={setPreciosOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" /> Precios — {preciosForm.estampado} / {preciosForm.color}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <p className="text-xs text-zinc-500">Configura los precios para esta playera. Estos precios aparecen en la tienda online. Deja en 0 los tipos que no quieras ofrecer.</p>
                        <div>
                            <Label className="form-label">Marca (opcional, agrupa en tienda)</Label>
                            <Input value={preciosForm.marca} onChange={e => setPreciosForm(f => ({ ...f, marca: e.target.value }))} placeholder="Karry Jeans" data-testid="input-precios-marca" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="form-label">Precio Pieza ($)</Label>
                                <Input type="number" min="0" step="0.01" value={preciosForm.precio} onChange={e => setPreciosForm(f => ({ ...f, precio: e.target.value }))} placeholder="180.00" data-testid="input-precios-pieza" />
                            </div>
                            <div>
                                <Label className="form-label">Tamaño Paquete</Label>
                                <Input type="number" min="1" value={preciosForm.tamano_paquete} onChange={e => setPreciosForm(f => ({ ...f, tamano_paquete: e.target.value }))} placeholder="12" data-testid="input-precios-tampaq" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="form-label">Precio Paquete ($)</Label>
                                <Input type="number" min="0" step="0.01" value={preciosForm.precio_paquete} onChange={e => setPreciosForm(f => ({ ...f, precio_paquete: e.target.value }))} placeholder="1800.00" data-testid="input-precios-paquete" />
                            </div>
                            <div>
                                <Label className="form-label">Precio Corrida ($) <span className="text-[10px] text-zinc-500">(opcional)</span></Label>
                                <Input type="number" min="0" step="0.01" value={preciosForm.precio_corrida} onChange={e => setPreciosForm(f => ({ ...f, precio_corrida: e.target.value }))} placeholder="auto" data-testid="input-precios-corrida" />
                            </div>
                        </div>
                        <p className="text-[10px] text-zinc-500">Si dejas <strong>Corrida</strong> en 0, se calcula automaticamente como mitad del paquete con $10 extra por pieza.</p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreciosOpen(false)}>Cancelar</Button>
                        <Button onClick={guardarPrecios} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-guardar-precios">Guardar precios</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
